import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { RootState } from 'store';
import {
  useGetMeQuery,
  useMarkSmsAlertsLaunchAnnouncementSeenMutation,
} from 'store/services/user/meApi';
import { useHaveListFetchInfiniteQuery } from 'store/services/collectionList/haveList/haveListApi';

export interface UseSmsAlertsLaunchAnnouncementGateResult {
  /** True iff the launch announcement modal should be displayed right now. */
  isOpen: boolean;
  /** "Set up SMS Alerts" — mark seen, navigate to Have List, open bulk-manage. */
  handlePrimary: () => void;
  /** "Maybe later", backdrop click, Escape — mark seen, no navigation. */
  handleDismiss: () => void;
}

const HAVE_LIST_PATH = '/my-cards/have-list';

/**
 * Drives the one-time SMS Alerts launch announcement modal.
 *
 * Trigger: `user.hasSeenSmsAlertsLaunchAnnouncement === false` AND the user
 * has at least one card in their Have List. Mount at the post-login layout so
 * it fires on first page load after login regardless of route.
 *
 * Separate flag and modal from {@link useSmsAlertsIntroGate} — the intro is a
 * consent gate that defers an action; this is a proactive announcement.
 */
export function useSmsAlertsLaunchAnnouncementGate(): UseSmsAlertsLaunchAnnouncementGateResult {
  const navigate = useNavigate();
  const location = useLocation();
  // Synchronous auth check — `state.auth.user` is hydrated from localStorage at
  // store creation, so it's a resolved value (object | null) on first render with
  // no async "unknown" window. Gating on it means the announcement can never flash
  // for logged-out visitors while `/me` resolves (or while stale post-logout `/me`
  // cache lingers — `useGetMeQuery` is otherwise not cleared on in-app logout).
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.user);
  // Skip the `/me` fetch entirely when logged out — avoids a wasted 401 and stops
  // us reading any stale cached `/me` from a previous session.
  const { data: user } = useGetMeQuery(undefined, { skip: !isAuthenticated });
  const [markSeen] = useMarkSmsAlertsLaunchAnnouncementSeenMutation();

  // Strict `=== false` — `undefined` (still loading, or backend not deployed)
  // must NOT trip the gate. Once true, the announcement never re-fires.
  const flagIsFalse = user?.hasSeenSmsAlertsLaunchAnnouncement === false;

  // Skip the Have List query entirely once we know the announcement won't
  // fire — avoids an extra request on every page load for the 99% of users
  // who have already dismissed.
  const { data: haveListPages } = useHaveListFetchInfiniteQuery(
    {},
    { skip: !flagIsFalse },
  );

  const hasAtLeastOneCard = useMemo(() => {
    if (!haveListPages?.pages?.length) return false;
    const firstPage = haveListPages.pages[0];
    return (firstPage?.totalCards ?? 0) > 0;
  }, [haveListPages]);

  const isOpen = isAuthenticated && flagIsFalse && hasAtLeastOneCard;

  const handlePrimary = useCallback(() => {
    // Mark seen first (optimistic) so the announcement disappears immediately
    // even if the navigation is slow.
    void markSeen();
    const targetUrl = `${HAVE_LIST_PATH}?openBulkManage=true`;
    if (location.pathname === HAVE_LIST_PATH) {
      // Already on Have List — just push the query param so AddCards picks it
      // up. `replace: false` keeps back-button behavior natural.
      navigate(targetUrl);
    } else {
      navigate(targetUrl);
    }
  }, [markSeen, navigate, location.pathname]);

  const handleDismiss = useCallback(() => {
    void markSeen();
  }, [markSeen]);

  return { isOpen, handlePrimary, handleDismiss };
}
